import { useEffect, useState } from "react";
import { Alert, Grid, Typography } from "@mui/material";
import dayjs from "dayjs";
import { toast } from "react-toastify";

import { BucketAwtENUM, useUploadFile } from "@/shared";
import { CustomPaperBox } from "@/shared/components/ui";
import { ScrollableDialogProps } from "@/shared/components/ui/Dialogs";
import { useUiStore } from "@/store/ui";
import { OrdenTrabajo } from "@/shared/interfaces";
import FotosVisitaMultiUploader from "../FotosVisitaMultiUploader";
import {
  FlujoFotos,
  agregarFotosTraslado,
  useRefrescarFotosTrasladoLive,
  useSetFotosTrasladoLive,
} from "./fotos-traslado.actions";

type EtapaCompletar = "INGRESO" | "FINALIZACION";

const TEXTOS: Record<EtapaCompletar, { titulo: string; nombre: string }> = {
  INGRESO: { titulo: "Volver a subir fotos de ingreso", nombre: "ingreso" },
  FINALIZACION: { titulo: "Volver a subir fotos de finalización", nombre: "finalización" },
};

type CompletarFotosTrasladoModalProps = {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  ordenTrabajo?: OrdenTrabajo;
  etapa: EtapaCompletar;
  /** Cuántas fotos eliminó Activaciones en la etapa (se exige exactamente esa cantidad). */
  faltan: number;
  /** Traslado (por defecto) o visita: decide el endpoint del backend. */
  flujo?: FlujoFotos;
};

/**
 * Modal del TÉCNICO para volver a subir las fotos que Activaciones eliminó en
 * una etapa ya registrada (ingreso o finalización). Exige exactamente las que
 * se eliminaron: cada foto nueva ocupa el lugar de una eliminada. Solo repone:
 * no reemplaza ni elimina fotos que siguen cargadas. Sirve para traslados y
 * visitas (`flujo`).
 */
export default function CompletarFotosTrasladoModal({
  open,
  onClose,
  onSuccess,
  ordenTrabajo,
  etapa,
  faltan,
  flujo = "traslado",
}: CompletarFotosTrasladoModalProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [showError, setShowError] = useState(false);
  const setIsGlobalLoading = useUiStore((s) => s.setIsGlobalLoading);
  const { handleUploadFile } = useUploadFile();
  const setFotosLive = useSetFotosTrasladoLive();
  const refrescarFotosLive = useRefrescarFotosTrasladoLive();

  const idOT = ordenTrabajo?.id_orden_trabajo;
  const PATH_BUCKET = `${BucketAwtENUM.IMAGE_INSTALACION}/${idOT}`;
  const { titulo, nombre } = TEXTOS[etapa];

  // Si mientras el modal está abierto cambia lo que falta (Activaciones eliminó
  // otra foto o ya se completó), se recorta la selección al nuevo tope.
  useEffect(() => {
    setFiles((prev) => (prev.length > faltan ? prev.slice(0, Math.max(0, faltan)) : prev));
  }, [faltan]);

  const cerrar = () => {
    setFiles([]);
    setShowError(false);
    onClose();
  };

  const handleConfirm = async () => {
    if (!idOT || faltan <= 0) return cerrar();
    if (files.length !== faltan) {
      setShowError(true);
      return;
    }
    setIsGlobalLoading(true);
    try {
      const subidas = await Promise.all(files.map((f) => handleUploadFile(f, PATH_BUCKET)));
      const fotos = subidas.map((r: any) => ({
        photoUrl: r?.url as string,
        photoId: r?.id_archivos || r?.url || null,
        fecha_hora: dayjs().toISOString(),
      }));
      if (fotos.some((f) => !f.photoUrl)) {
        toast.error("No se pudo subir alguna de las fotos. Inténtalo de nuevo.");
        return;
      }
      const snapshot = await agregarFotosTraslado(idOT, { etapa, fotos }, flujo);
      setFotosLive(snapshot);
      toast.success(`Fotos de ${nombre} guardadas.`);
      cerrar();
      onSuccess?.();
    } catch (e) {
      // El backend ya informó el motivo (toast del cliente HTTP); se refresca el
      // estado por si cambió lo que falta.
      console.error(`Error al completar fotos de ${flujo}`, e);
      refrescarFotosLive(idOT, flujo);
    } finally {
      setIsGlobalLoading(false);
    }
  };

  return (
    <ScrollableDialogProps
      open={open}
      title={titulo}
      minWidth="60%"
      contentNode={
        <CustomPaperBox sxPaper={{ p: 3 }}>
          <Grid item xs={12}>
            <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
              Activaciones eliminó <b>{faltan}</b> {faltan === 1 ? "foto" : "fotos"} de {nombre}.
              Sube <b>exactamente {faltan}</b> para reponer{faltan === 1 ? "la" : "las"} y completar la evidencia.
            </Alert>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              Fotos de {nombre} por volver a subir
            </Typography>
            <FotosVisitaMultiUploader
              files={files}
              setFiles={setFiles}
              min={Math.max(1, faltan)}
              max={Math.max(1, faltan)}
              label={`Fotos de ${nombre}`}
            />
            {showError && files.length !== faltan && (
              <Typography color="error" fontWeight={700} sx={{ mt: 1 }}>
                Debes subir exactamente {faltan} {faltan === 1 ? "foto" : "fotos"} (llevas {files.length}).
              </Typography>
            )}
          </Grid>
        </CustomPaperBox>
      }
      onClose={cerrar}
      onConfirm={handleConfirm}
      confirmTextBtn="Guardar fotos"
      confirmVariantBtn="outlined"
    />
  );
}
